import Button from "components/Button/Button";
import DeleteCVModal from "components/Modal/DeleteFileModal";
import React, { forwardRef, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { AiFillDelete, AiOutlineFilePdf } from "react-icons/ai";
import actionApi from "services/actionApi";
import { byteToString } from "utils/converter";
import makeToast from "utils/toast/makeToast";

const MAX_SIZE = 30000000;
const MAX_FILE = 1;

const InputFile = forwardRef((props, ref) => {
  const {
    title,
    name,
    type = "cv",
    email = "",
    errors,
    placeholder,
    setValue,
    defaultValue,
    defaultName = "",
    defaultSize = 1000000,
    isModal = false,
    maxSize = "5mb",
    ...rest
  } = props;
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
    useDropzone({
      maxFiles: MAX_FILE,
      maxSize: MAX_SIZE,
    });

  const handleUpload = async (newFile) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", newFile);
    formData.append("email", email);
    formData.append("type", type);
    await actionApi
      .uploadFile(formData)
      .then((response) => {
        const FILE_ID = response?.data?.file_id;
        setValue(name, FILE_ID, {
          shouldValidate: true,
        });
        setFile(newFile);
      })
      .catch((err) => {
        makeToast({
          type: "error",
          label: err?.response?.data?.errors?.[0],
        });
      });
    setIsUploading(false);
  };

  const handleRemove = () => {
    setFile(null);
    setValue(name, null);
  };

  useEffect(() => {
    if (acceptedFiles?.length > 0) {
      handleUpload(acceptedFiles?.[0]);
    }
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections?.[0]?.errors?.[0]?.code === "file-too-large") {
      makeToast({
        label: `File too large. Please upload file with size less than ${maxSize}`,
        type: "error",
      });
    }
  }, [fileRejections]);

  useEffect(() => {
    if (defaultValue) {
      setValue(name, defaultValue);
      setFile({
        name: defaultName,
        size: defaultSize,
      });
    }
  }, [defaultValue]);

  return (
    <div className="flex flex-col" {...rest}>
      <DeleteCVModal
        open={showModal}
        onClose={() => setShowModal(false)}
        handleRemove={handleRemove}
        type={type}
      />
      {title && (
        <label htmlFor={name} className="text-sm text-wce-neutral-9 mb-2">
          {title}
        </label>
      )}
      <div className="bg-wce-neutral-2 border border-wce-neutral-5 p-4 rounded-sm mb-2">
        {isUploading ? (
          <div
            {...getRootProps({
              className: "dropzone flex flex-col items-center text-center",
            })}
          >
            <input {...getInputProps()} />
            <AiOutlineFilePdf color="#1890FF" size={48} className="mb-5" />
            <span className="mb-1 text-wce-neutral-10">Uploading...</span>
            <span className="text-sm text-wce-neutral-7">
              Please wait for a moment
            </span>
          </div>
        ) : !file ? (
          <div
            {...getRootProps({
              className: "dropzone flex flex-col items-center text-center",
            })}
          >
            <input {...getInputProps()} />
            <AiOutlineFilePdf color="#1890FF" size={48} className="mb-5" />
            <span className="mb-1 text-wce-neutral-10">{placeholder}</span>
            <span className="text-sm text-wce-neutral-7">
              Click or drag file to this area to upload
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <AiOutlineFilePdf color="#1890FF" size={48} className="mb-5" />
            <span className="mb-1 text-wce-neutral-10 break-all">
              {file?.name}
            </span>
            <span className="text-sm text-wce-neutral-7">
              {byteToString(file?.size)}
            </span>
          </div>
        )}
      </div>
      {file && !isUploading && (
        <Button
          onClick={() => {
            isModal ? setShowModal(true) : handleRemove();
          }}
          color="danger"
          variant="link"
          className="flex gap-2 items-start mb-1"
        >
          <AiFillDelete color="FF4D4F" size={16} />
          <span className="text-sm">Delete File</span>
        </Button>
      )}
      {errors?.[name] && !isUploading && (
        <span className="text-sm text-wce-pink-bright">
          Please input your file.
        </span>
      )}
      <div className="text-wce-neutral-7 text-sm self-end">*max {maxSize}</div>
    </div>
  );
});

export default InputFile;
